import React, { useCallback } from 'react';
import classNames from 'classnames';
import {
  CheckboxConfig,
  DropDownConfig,
  MultiSelectConfig,
  SliderConfig,
  TextConfig,
  WidgetGroupSetting,
  WidgetGroupState,
} from '@kemu-io/kemu-core/types';
import NumericField from '../DynamicFields/NumericField/NumericField';
import TextField from '../DynamicFields/TextField/TextField';
import SliderField from '../DynamicFields/SliderField/SliderField';
import CheckboxField from '../DynamicFields/CheckboxField/CheckboxField';
import DropdownField from '../DynamicFields/DropdownField/DropdownField';
import MultiSelectField from '../DynamicFields/MultiSelectField/MultiSelectField';
import styles from './GroupFieldsRenderer.module.css';
import { ABORT_OPEN_WIDGET_CLICK } from '@common/constants';
import useSetWidgetState from '@hooks/useSetWidgetState';

type Props = {
  fields: WidgetGroupSetting[];
  thingRecipeId: string;
  recipeId: string;
  widgetId: string;
  color?: string;
  isBrightColor?: boolean;
  disabled?: boolean;
  multiPort: boolean;
  isInnerSetting: boolean;
  repaintWidgetPorts: () => void;
}

const GroupFieldsRenderer = (props: Props) => {
  const {
    fields, recipeId, thingRecipeId,
    color, disabled, multiPort, repaintWidgetPorts,
    isBrightColor, isInnerSetting, widgetId
  } = props;

  const isSingleElement = fields.length === 1 && !multiPort && !isInnerSetting;
  const setWidgetState = useSetWidgetState<WidgetGroupState>(recipeId, thingRecipeId, widgetId);

  const handleFieldChange = useCallback((settingIndex: number, newConfig: Record<string, unknown>) => {
    setWidgetState((current) => {
      const newSettings = [...current.settings || []];

      // Find the actual index in the full settings array by matching variableName and type
      // settingIndex is from the filtered fields array, so we need to find the real index
      const fieldFromFilteredArray = fields[settingIndex];
      if (!fieldFromFilteredArray) { return current; }

      const actualIndex = newSettings.findIndex(
        (setting) =>
          setting.variableName === fieldFromFilteredArray.variableName &&
          setting.type === fieldFromFilteredArray.type
      );

      if (actualIndex === -1) { return current; }

      newSettings[actualIndex] = {
        ...newSettings[actualIndex],
        config: {
          ...(newSettings[actualIndex].config || {}),
          ...newConfig,
        }
      };
      return {
        ...current,
        settings: newSettings,
      };
    });

  }, [setWidgetState, fields]);

  return (
    <>
      {fields.map((setting, index) => (
        <div
          key={`field-${index}-${setting.type}`}
          className={classNames(styles.FieldItem, ABORT_OPEN_WIDGET_CLICK)}
        >
          {setting.type === 'number' && (
            <NumericField
              label={setting.label}
              recipeId={recipeId}
              widgetId={widgetId}
              color={color}
              thingRecipeId={thingRecipeId}
              isSingleElement={isSingleElement}
              variableName={setting.variableName}
              config={setting.config as SliderConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}

          {setting.type === 'text' && (
            <TextField
              isHiddenField={props.isInnerSetting}
              onRepaintWidgetPorts={repaintWidgetPorts}
              label={setting.label}
              recipeId={recipeId}
              thingRecipeId={thingRecipeId}
              widgetId={widgetId}
              variableName={setting.variableName}
              isSingleElement={isSingleElement}
              color={color}
              config={setting.config as TextConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}

          {setting.type === 'slider' && (
            <SliderField
              label={setting.label}
              disabled={disabled}
              recipeId={recipeId}
              thingRecipeId={thingRecipeId}
              widgetId={widgetId}
              variableName={setting.variableName}
              color={color}
              config={setting.config as SliderConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}

          {setting.type === 'dropdown' && (
            <DropdownField
              label={setting.label}
              disabled={disabled}
              recipeId={recipeId}
              thingRecipeId={thingRecipeId}
              widgetId={widgetId}
              variableName={setting.variableName}
              isSingleElement={isSingleElement}
              color={color}
              config={setting.config as DropDownConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}

          {setting.type === 'multiselect' && (
            <MultiSelectField
              label={setting.label}
              disabled={disabled}
              recipeId={recipeId}
              thingRecipeId={thingRecipeId}
              widgetId={widgetId}
              variableName={setting.variableName}
              isSingleElement={isSingleElement}
              color={color}
              config={setting.config as MultiSelectConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}

          {setting.type === 'checkbox' && (
            <CheckboxField
              label={setting.label}
              disabled={disabled}
              recipeId={recipeId}
              thingRecipeId={thingRecipeId}
              widgetId={widgetId}
              variableName={setting.variableName}
              isSingleElement={isSingleElement}
              color={color}
              isBrightColor={isBrightColor}
              config={setting.config as CheckboxConfig }
              settingsIndex={index}
              onUpdateConfig={handleFieldChange}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default GroupFieldsRenderer;
